import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, DollarSign, MapPin, User, Phone, Building, Tag } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { cartAPI, ordersAPI, authAPI } from '../services/api';
import { useCartSession } from '../hooks/useAPI';
import Header from './Header';
import Footer from './Footer';
import LoadingSpinner from './LoadingSpinner';
import { useToast } from '../hooks/use-toast';
import { calculateCartTotals, formatPrice } from '../utils/discountUtils';
import PayPalCheckout from './PayPalCheckout';
import FacebookPixelService from '../services/facebookPixel';

const Checkout = () => {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading: authLoading, initialized } = useAuth();
  const { toast } = useToast();
  const sessionId = useCartSession();
  
  // Check if this is samples-only checkout
  const urlParams = new URLSearchParams(window.location.search);
  const samplesOnly = urlParams.get('samples') === 'true';
  
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('bank-transfer'); // 'bank-transfer' or 'paypal'
  
  // Coupon state
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  
  // State for global discounts
  const [globalDiscounts, setGlobalDiscounts] = useState([]);
  
  // Helper function to check if an item is a sample (same logic as Cart.js)
  const isSampleItem = (item) => {
    // Priority 1: Flag isSample
    if (item.isSample === true) return true;
    
    // Priority 2: Collection flag
    if (item.productData?.isSamplesCollection === true) return true;
    
    // Priority 3: Collection name/slug
    const collectionSlug = String(item.productData?.collectionName || '').toLowerCase();
    const collectionTitle = String(item.productData?.collectionTitle || '').toLowerCase();
    if (collectionSlug === 'campioni' || collectionTitle.includes('campion')) return true;
    
    // Priority 4: Name/format
    const format = String(item.productData?.format || item.format || '').toLowerCase();
    const name = String(item.productData?.name || item.name || '').toLowerCase();
    const description = String(item.productData?.description || item.description || '').toLowerCase();
    
    return format.includes('5cl') || format.includes('campion') ||
           name.includes('campion') || description.includes('campion');
  };
  
  // Load global discounts on component mount
  useEffect(() => {
    const loadGlobalDiscounts = async () => {
      try {
        const { getGlobalQuantityDiscounts } = await import('../utils/discountUtils');
        const discounts = await getGlobalQuantityDiscounts();
        setGlobalDiscounts(discounts);
      } catch (error) {
        console.warn('Failed to load global discounts:', error);
        // Use default discounts as fallback
        const { getDefaultQuantityDiscounts } = await import('../utils/discountUtils');
        setGlobalDiscounts(getDefaultQuantityDiscounts());
      }
    };
    
    loadGlobalDiscounts();
  }, []);

  // Calculate cart totals with quantity discounts (SAME AS CART!)
  const cartTotals = useMemo(() => {
    return calculateCartTotals(cartItems, globalDiscounts);
  }, [cartItems, globalDiscounts]);
  
  const [formData, setFormData] = useState({
    // Billing Address
    billingAddress: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      company: '',
      street: '',
      streetNumber: '',
      city: '',
      province: '',
      zipCode: '',
      country: 'IT',
      phone: user?.phone || '',
      notes: ''
    },
    
    // Shipping Address
    shippingAddress: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      company: '',
      street: '',
      streetNumber: '',
      city: '',
      province: '',
      zipCode: '',
      country: 'IT',
      phone: user?.phone || '',
      notes: ''
    },
    
    // Order details
    paymentMethod: 'bank_transfer', // 'bank_transfer', 'paypal'
    customerNotes: '',
    newsletterOptIn: false,
    sameAsShipping: true,
    saveAddresses: true // Default to saving addresses
  });
  
  const [errors, setErrors] = useState({});

  // Handle payment method change with smooth scroll
  const handlePaymentMethodChange = (paymentMethod) => {
    setFormData(prev => ({ ...prev, paymentMethod }));
    // Scroll to payment section smoothly to show the change
    setTimeout(() => {
      const paymentSection = document.getElementById('payment-section');
      if (paymentSection) {
        paymentSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Handle Stripe checkout for credit card payments
  const handleStripeCheckout = async (orderId) => {
    try {
      setProcessing(true);
      
      // Get current origin URL
      const originUrl = window.location.origin;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout/cart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          order_id: orderId,
          origin_url: originUrl,
          metadata: {
            source: 'cart_checkout',
            payment_method: 'credit_card'
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Stripe checkout error:', error);
      toast({
        variant: "destructive",
        title: "Errore pagamento",
        description: error.message || "Si è verificato un errore durante l'elaborazione del pagamento."
      });
    } finally {
      setProcessing(false);
    }
  };

  // Load cart data and user addresses
  useEffect(() => {
    // Wait for auth initialization before checking authentication
    if (!initialized || authLoading) {
      return;
    }

    // If auth is initialized and user is not authenticated, redirect to login
    if (!isAuthenticated()) {
      navigate('/login?redirect=/checkout');
      return;
    }

    const loadCartAndAddresses = async () => {
      try {
        setLoading(true);
        
        // Load cart
        const cartData = await cartAPI.getCart(sessionId);
        
        if (!cartData.items || cartData.items.length === 0) {
          toast({
            title: "Carrello vuoto",
            description: "Non ci sono prodotti nel carrello.",
            variant: "destructive"
          });
          navigate('/carrello');
          return;
        }
        
        // Filter items based on checkout mode
        let itemsToCheckout = cartData.items;
        
        if (samplesOnly) {
          // Only include samples
          itemsToCheckout = cartData.items.filter(isSampleItem);
          console.log('🧪 SAMPLES ONLY CHECKOUT:', {
            totalItems: cartData.items.length,
            samplesItems: itemsToCheckout.length
          });
          
          if (itemsToCheckout.length === 0) {
            toast({
              title: "Nessun campione",
              description: "Non ci sono campioni da ordinare.",
              variant: "destructive"
            });
            navigate('/carrello');
            return;
          }
        }
        
        setCartItems(itemsToCheckout);

        // Load applied coupon from sessionStorage
        try {
          const storedCoupon = sessionStorage.getItem('appliedCoupon');
          if (storedCoupon) {
            const couponData = JSON.parse(storedCoupon);
            if (couponData && couponData.valid) {
              setAppliedCoupon(couponData);
            }
          }
        } catch (couponError) {
          console.error('Error loading coupon from sessionStorage:', couponError);
        }

        // Load saved addresses
        try {
          const addressResponse = await authAPI.getAddresses(token);
          if (addressResponse.success && addressResponse.data) {
            const { shippingAddress, billingAddress, sameAsShipping } = addressResponse.data;
            
            if (shippingAddress) {
              // Auto-compile shipping address
              setFormData(prev => ({
                ...prev,
                shippingAddress: {
                  firstName: shippingAddress.firstName || user?.firstName || '',
                  lastName: shippingAddress.lastName || user?.lastName || '',
                  company: shippingAddress.company || '',
                  street: shippingAddress.address || '',
                  streetNumber: '', // Keep separate for this form
                  city: shippingAddress.city || '',
                  province: shippingAddress.province || '',
                  zipCode: shippingAddress.postalCode || '',
                  country: shippingAddress.country || 'IT',
                  phone: shippingAddress.phone || user?.phone || '',
                  notes: shippingAddress.notes || ''
                },
                billingAddress: sameAsShipping && shippingAddress ? {
                  firstName: shippingAddress.firstName || user?.firstName || '',
                  lastName: shippingAddress.lastName || user?.lastName || '',
                  company: shippingAddress.company || '',
                  street: shippingAddress.address || '',
                  streetNumber: '', 
                  city: shippingAddress.city || '',
                  province: shippingAddress.province || '',
                  zipCode: shippingAddress.postalCode || '',
                  country: shippingAddress.country || 'IT',
                  phone: shippingAddress.phone || user?.phone || '',
                  notes: shippingAddress.notes || ''
                } : billingAddress ? {
                  firstName: billingAddress.firstName || user?.firstName || '',
                  lastName: billingAddress.lastName || user?.lastName || '',
                  company: billingAddress.company || '',
                  street: billingAddress.address || '',
                  streetNumber: '',
                  city: billingAddress.city || '',
                  province: billingAddress.province || '',
                  zipCode: billingAddress.postalCode || '',
                  country: billingAddress.country || 'IT',
                  phone: billingAddress.phone || user?.phone || '',
                  notes: billingAddress.notes || ''
                } : prev.billingAddress,
                sameAsShipping: sameAsShipping
              }));

              toast({
                title: "Indirizzi caricati",
                description: "I tuoi indirizzi salvati sono stati compilati automaticamente.",
                variant: "default"
              });
            }
          }
        } catch (addressError) {
          console.log('No saved addresses found, using defaults');
          // Non è un errore critico, continua normalmente
        }
        
      } catch (error) {
        console.error('Failed to load cart:', error);
        toast({
          title: "Errore",
          description: "Impossibile caricare il carrello.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    loadCartAndAddresses();
  }, [sessionId, isAuthenticated, navigate, toast, user, initialized, authLoading]);

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    
    // Clear error when user starts typing
    if (errors[`${section}.${field}`]) {
      setErrors(prev => ({ ...prev, [`${section}.${field}`]: '' }));
    }
  };

  const handleSameAsShippingChange = (checked) => {
    setFormData(prev => ({
      ...prev,
      sameAsShipping: checked,
      billingAddress: checked ? { ...prev.shippingAddress } : prev.billingAddress
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate shipping address
    const requiredShippingFields = ['firstName', 'lastName', 'street', 'city', 'province', 'zipCode', 'phone'];
    requiredShippingFields.forEach(field => {
      if (!formData.shippingAddress[field]) {
        newErrors[`shippingAddress.${field}`] = 'Campo richiesto';
      }
    });
    
    // Special validation for street + streetNumber combination
    const fullShippingAddress = `${formData.shippingAddress.street}${formData.shippingAddress.streetNumber ? ' ' + formData.shippingAddress.streetNumber : ''}`.trim();
    if (!fullShippingAddress || fullShippingAddress.length < 5) {
      newErrors['shippingAddress.street'] = 'Indirizzo completo richiesto';
    }
    
    // Validate billing address (if different from shipping)
    if (!formData.sameAsShipping) {
      const requiredBillingFields = ['firstName', 'lastName', 'street', 'city', 'province', 'zipCode', 'phone'];
      requiredBillingFields.forEach(field => {
        if (!formData.billingAddress[field]) {
          newErrors[`billingAddress.${field}`] = 'Campo richiesto';
        }
      });
      
      // Special validation for billing address
      const fullBillingAddress = `${formData.billingAddress.street}${formData.billingAddress.streetNumber ? ' ' + formData.billingAddress.streetNumber : ''}`.trim();
      if (!fullBillingAddress || fullBillingAddress.length < 5) {
        newErrors['billingAddress.street'] = 'Indirizzo completo richiesto';
      }
    }
    
    // Validate province (2 characters)
    if (formData.shippingAddress.province && formData.shippingAddress.province.length !== 2) {
      newErrors['shippingAddress.province'] = 'Provincia deve essere di 2 caratteri (es. MI)';
    }
    
    // Validate ZIP code (5 digits)
    if (formData.shippingAddress.zipCode && !/^\d{5}$/.test(formData.shippingAddress.zipCode)) {
      newErrors['shippingAddress.zipCode'] = 'CAP deve essere di 5 cifre';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateTotals = () => {
    // Use the new discount calculation system
    const subtotalWithDiscounts = cartTotals.subtotal;
    const originalSubtotal = cartTotals.originalTotal;
    const totalDiscount = cartTotals.totalDiscount;
    
    // Ensure all values are valid numbers
    const validSubtotal = isNaN(subtotalWithDiscounts) ? 0 : subtotalWithDiscounts;
    const shippingCost = 0; // Free shipping for all orders
    
    // Apply coupon discount if available
    const couponDiscount = appliedCoupon && appliedCoupon.valid 
      ? (appliedCoupon.discount_amount || 0) 
      : 0;
    
    // Calculate IVA included in prices (22% IVA for Italy)
    // Since prices are IVA-inclusive, we extract the IVA amount for display purposes
    const taxAmount = (validSubtotal / 1.22) * 0.22;
    const total = Math.max(0, validSubtotal + shippingCost - couponDiscount);
    
    return { 
      subtotal: validSubtotal,
      originalSubtotal: originalSubtotal,
      totalDiscount: totalDiscount,
      couponDiscount: couponDiscount,
      shippingCost: shippingCost, 
      taxAmount: taxAmount, 
      total: total 
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if user is authenticated
    if (!isAuthenticated()) {
      toast({
        title: "Accesso richiesto",
        description: "Devi essere loggato per completare l'ordine.",
        variant: "destructive"
      });
      
      // Redirect to login with return URL
      navigate('/login?redirect=/checkout');
      return;
    }
    
    if (!validateForm()) {
      toast({
        title: "Errori nel modulo",
        description: "Controlla i campi evidenziati in rosso.",
        variant: "destructive"
      });
      // Scroll to top to show errors with smooth animation
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // For PayPal, the payment will be handled by PayPalCheckout component
    if (formData.paymentMethod === 'paypal') {
      // Validation passed, PayPal button will handle the rest
      toast({
        title: "Dati validati",
        description: "Procedi con il pagamento PayPal qui sotto.",
        variant: "default"
      });
      
      // Scroll to PayPal button
      setTimeout(() => {
        const paypalSection = document.getElementById('paypal-payment-section');
        if (paypalSection) {
          paypalSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }
    
    setProcessing(true);
    
    try {
      // Track Facebook Pixel - InitiateCheckout event
      FacebookPixelService.trackInitiateCheckout({
        value: cartTotals.grandTotal,
        currency: 'EUR',
        num_items: cartItems.length,
        content_ids: cartItems.map(item => item.id)
      });

      const orderData = {
        billingAddress: formData.sameAsShipping ? formData.shippingAddress : formData.billingAddress,
        shippingAddress: formData.shippingAddress,
        paymentMethod: formData.paymentMethod,
        customerNotes: formData.customerNotes,
        newsletterOptIn: formData.newsletterOptIn,
        appliedCoupon: appliedCoupon  // Include coupon data
      };
      
      const result = await ordersAPI.createOrder(sessionId, orderData);
      
      if (result.success) {
        // Save addresses to user profile automatically if user opted in
        if (formData.saveAddresses) {
          try {
          const addressesToSave = {
            shippingAddress: {
              firstName: formData.shippingAddress.firstName,
              lastName: formData.shippingAddress.lastName,
              company: formData.shippingAddress.company || '',
              address: `${formData.shippingAddress.street} ${formData.shippingAddress.streetNumber}`.trim(),
              city: formData.shippingAddress.city,
              postalCode: formData.shippingAddress.zipCode,
              province: formData.shippingAddress.province,
              country: formData.shippingAddress.country || 'Italy',
              phone: formData.shippingAddress.phone || '',
              notes: formData.shippingAddress.notes || ''
            },
            billingAddress: formData.sameAsShipping ? {
              firstName: formData.shippingAddress.firstName,
              lastName: formData.shippingAddress.lastName,
              company: formData.shippingAddress.company || '',
              address: `${formData.shippingAddress.street} ${formData.shippingAddress.streetNumber}`.trim(),
              city: formData.shippingAddress.city,
              postalCode: formData.shippingAddress.zipCode,
              province: formData.shippingAddress.province,
              country: formData.shippingAddress.country || 'Italy',
              phone: formData.shippingAddress.phone || '',
              notes: formData.shippingAddress.notes || ''
            } : {
              firstName: formData.billingAddress.firstName,
              lastName: formData.billingAddress.lastName,
              company: formData.billingAddress.company || '',
              address: `${formData.billingAddress.street} ${formData.billingAddress.streetNumber}`.trim(),
              city: formData.billingAddress.city,
              postalCode: formData.billingAddress.zipCode,
              province: formData.billingAddress.province,
              country: formData.billingAddress.country || 'Italy',
              phone: formData.billingAddress.phone || '',
              notes: formData.billingAddress.notes || ''
            },
            sameAsShipping: formData.sameAsShipping
          };

          await authAPI.updateAddresses(token, addressesToSave);
          console.log('✅ Addresses saved automatically after order creation');
          
          // Notify user that addresses were saved
          setTimeout(() => {
            toast({
              title: "Indirizzi salvati",
              description: "I tuoi indirizzi sono stati salvati automaticamente per i prossimi ordini.",
              variant: "default"
            });
          }, 2000);
        } catch (addressError) {
          console.warn('⚠️ Could not save addresses automatically:', addressError);
          // Non bloccare il flusso dell'ordine se il salvataggio indirizzi fallisce
        }
        }

        // Track Facebook Pixel - Purchase event (MOST IMPORTANT!)
        FacebookPixelService.trackPurchase({
          value: result.order.totalAmount || cartTotals.grandTotal,
          currency: 'EUR',
          content_type: 'product',
          content_ids: cartItems.map(item => item.id),
          num_items: cartItems.length,
          order_id: result.order.orderNumber
        });

        toast({
          title: "Ordine creato!",
          description: `Ordine ${result.order.orderNumber} creato con successo.`
        });
        
        // Clear applied coupon from sessionStorage after successful order
        sessionStorage.removeItem('appliedCoupon');
        
        // Redirect based on payment method
        switch (formData.paymentMethod) {
          case 'bank_transfer':
            navigate(`/order-confirmation/${result.order.orderNumber}`, { 
              state: { order: result.order, paymentInstructions: result.order.bankTransferInstructions }
            });
            break;
          case 'credit_card':
            // Create Stripe checkout session directly
            await handleStripeCheckout(result.order.id);
            break;
          case 'paypal':
            // Redirect to PayPal
            window.location.href = result.order.paypalApprovalUrl;
            break;
          default:
            navigate(`/order-confirmation/${result.order.orderNumber}`, { state: { order: result.order } });
        }
      } else {
        throw new Error(result.message);
      }
      
    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        title: "Errore nell'ordine",
        description: error.response?.data?.detail || "Errore durante la creazione dell'ordine",
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
    }
  };

  // PayPal success handler
  const handlePayPalSuccess = async (paymentData) => {
    try {
      setProcessing(true);
      
      // Create the order with PayPal payment confirmed
      const orderData = {
        billingAddress: formData.sameAsShipping ? formData.shippingAddress : formData.billingAddress,
        shippingAddress: formData.shippingAddress,
        paymentMethod: 'paypal',
        customerNotes: formData.customerNotes,
        newsletterOptIn: formData.newsletterOptIn,
        appliedCoupon: appliedCoupon,  // Include coupon data
        paymentDetails: {
          paypalOrderId: paymentData.paypalOrderId,
          captureId: paymentData.captureId,
          amount: paymentData.amount,
          currency: paymentData.currency,
          status: paymentData.status
        }
      };

      const response = await ordersAPI.createOrder(sessionId, orderData, token);
      
      if (response.success) {
        // Save addresses if requested
        if (formData.saveAddresses && isAuthenticated && token) {
          try {
            const addressesToSave = {
              shippingAddress: {
                firstName: formData.shippingAddress.firstName,
                lastName: formData.shippingAddress.lastName,
                company: formData.shippingAddress.company || '',
                address: `${formData.shippingAddress.street} ${formData.shippingAddress.streetNumber}`.trim(),
                city: formData.shippingAddress.city,
                postalCode: formData.shippingAddress.zipCode,
                province: formData.shippingAddress.province,
                country: formData.shippingAddress.country || 'Italy',
                phone: formData.shippingAddress.phone || '',
                notes: formData.shippingAddress.notes || ''
              },
              billingAddress: formData.sameAsShipping ? {
                firstName: formData.shippingAddress.firstName,
                lastName: formData.shippingAddress.lastName,
                company: formData.shippingAddress.company || '',
                address: `${formData.shippingAddress.street} ${formData.shippingAddress.streetNumber}`.trim(),
                city: formData.shippingAddress.city,
                postalCode: formData.shippingAddress.zipCode,
                province: formData.shippingAddress.province,
                country: formData.shippingAddress.country || 'Italy',
                phone: formData.shippingAddress.phone || '',
                notes: formData.shippingAddress.notes || ''
              } : {
                firstName: formData.billingAddress.firstName,
                lastName: formData.billingAddress.lastName,
                company: formData.billingAddress.company || '',
                address: `${formData.billingAddress.street} ${formData.billingAddress.streetNumber}`.trim(),
                city: formData.billingAddress.city,
                postalCode: formData.billingAddress.zipCode,
                province: formData.billingAddress.province,
                country: formData.billingAddress.country || 'Italy',
                phone: formData.billingAddress.phone || '',
                notes: formData.billingAddress.notes || ''
              },
              sameAsShipping: formData.sameAsShipping
            };

            await authAPI.updateAddresses(token, addressesToSave);
          } catch (error) {
            console.error('Error saving addresses:', error);
          }
        }

        toast({
          title: "Pagamento completato!",
          description: `Ordine ${response.order.orderNumber} pagato con successo tramite PayPal.`
        });
        
        // Clear applied coupon from sessionStorage after successful order
        sessionStorage.removeItem('appliedCoupon');
        
        navigate(`/order-confirmation/${response.order.orderNumber}`, { 
          state: { order: response.order, paymentMethod: 'paypal' }
        });
      }
    } catch (error) {
      console.error('Error processing PayPal order:', error);
      toast({
        title: "Errore nell'ordine",
        description: "Errore durante la finalizzazione dell'ordine con PayPal",
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
    }
  };

  // PayPal error handler
  const handlePayPalError = (error) => {
    console.error('PayPal payment error:', error);
    toast({
      title: "Errore PayPal",
      description: "Si è verificato un errore durante il pagamento PayPal",
      variant: "destructive"
    });
  };

  // PayPal cancel handler
  const handlePayPalCancel = () => {
    toast({
      title: "Pagamento annullato",
      description: "Il pagamento PayPal è stato annullato",
      variant: "default"
    });
  };

  // Prepare PayPal order data
  const paypalOrderData = useMemo(() => {
    if (formData.paymentMethod !== 'paypal' || !cartItems.length) return null;

    return {
      items: cartItems.map(item => ({
        name: item.type === 'custom' ? 
          `${item.productData.liquor.name} Personalizzato` : 
          `${item.recipe?.name} - ${item.collection}`,
        price: item.price / item.quantity, // unit price
        quantity: item.quantity,
        description: item.type === 'custom' ? 
          `Liquore personalizzato con ${item.productData.ingredients?.length || 0} botaniche` :
          'Prodotto della collezione premium'
      })),
      total: cartTotals.finalTotal,
      customerEmail: user?.email,
      orderId: `ORDER_${Date.now()}`
    };
  }, [formData.paymentMethod, cartItems, cartTotals, user?.email]);

  if (!isAuthenticated()) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Accesso Richiesto</h1>
            <p className="text-lg text-gray-600">
              Devi effettuare l'accesso per procedere al checkout.
            </p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <LoadingSpinner message="Caricamento checkout..." />
        </div>
        <Footer />
      </div>
    );
  }

  const { subtotal, originalSubtotal, totalDiscount, couponDiscount, shippingCost, taxAmount, total } = calculateTotals();

  // Show loading while auth is initializing
  if (!initialized || authLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <LoadingSpinner message="Verifica autenticazione..." />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Checkout</h1>
          <p className="text-lg text-gray-600">Completa il tuo ordine</p>
          
          {/* Samples Only Banner */}
          {samplesOnly && (
            <div className="mt-4 bg-green-50 border-2 border-green-400 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">🧪</span>
                <div>
                  <h3 className="font-semibold text-green-900">Ordine Campioni</h3>
                  <p className="text-sm text-green-700">
                    Stai ordinando {cartItems.length} {cartItems.length === 1 ? 'campione' : 'campioni'}. 
                    I prodotti normali rimangono nel carrello per il preventivo.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-8">
              
              {/* Shipping Address */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <MapPin className="w-6 h-6 mr-2 text-purple-600" />
                  Indirizzo di Spedizione
                </h2>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nome *</label>
                    <input
                      type="text"
                      value={formData.shippingAddress.firstName}
                      onChange={(e) => handleInputChange('shippingAddress', 'firstName', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 ${errors['shippingAddress.firstName'] ? 'border-red-500' : 'border-gray-300'}`}
                      disabled={processing}
                    />
                    {errors['shippingAddress.firstName'] && (
                      <p className="mt-1 text-sm text-red-600">{errors['shippingAddress.firstName']}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cognome *</label>
                    <input
                      type="text"
                      value={formData.shippingAddress.lastName}
                      onChange={(e) => handleInputChange('shippingAddress', 'lastName', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 ${errors['shippingAddress.lastName'] ? 'border-red-500' : 'border-gray-300'}`}
                      disabled={processing}
                    />
                    {errors['shippingAddress.lastName'] && (
                      <p className="mt-1 text-sm text-red-600">{errors['shippingAddress.lastName']}</p>
                    )}
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Azienda (opzionale)</label>
                    <input
                      type="text"
                      value={formData.shippingAddress.company}
                      onChange={(e) => handleInputChange('shippingAddress', 'company', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      disabled={processing}
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Via e Numero Civico *</label>
                    <input
                      type="text"
                      value={formData.shippingAddress.street || ''}
                      onChange={(e) => {
                        // Just update street field directly - no manipulation
                        handleInputChange('shippingAddress', 'street', e.target.value);
                        // Clear streetNumber while typing to avoid conflicts
                        if (formData.shippingAddress.streetNumber) {
                          handleInputChange('shippingAddress', 'streetNumber', '');
                        }
                      }}
                      onBlur={(e) => {
                        const fullAddress = e.target.value.trim();
                        // Try to split address into street and number only when user finishes typing
                        const parts = fullAddress.split(' ');
                        if (parts.length > 1 && /^\d/.test(parts[parts.length - 1])) {
                          // Last part starts with number, consider it street number
                          const streetNumber = parts.pop();
                          const street = parts.join(' ');
                          handleInputChange('shippingAddress', 'street', street);
                          handleInputChange('shippingAddress', 'streetNumber', streetNumber);
                        }
                      }}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 ${errors['shippingAddress.street'] || errors['shippingAddress.streetNumber'] ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Es: Via Roma 123"
                      disabled={processing}
                    />
                    {(errors['shippingAddress.street'] || errors['shippingAddress.streetNumber']) && (
                      <p className="mt-1 text-sm text-red-600">Via e numero civico sono richiesti</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Città *</label>
                    <input
                      type="text"
                      value={formData.shippingAddress.city}
                      onChange={(e) => handleInputChange('shippingAddress', 'city', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 ${errors['shippingAddress.city'] ? 'border-red-500' : 'border-gray-300'}`}
                      disabled={processing}
                    />
                    {errors['shippingAddress.city'] && (
                      <p className="mt-1 text-sm text-red-600">{errors['shippingAddress.city']}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Provincia *</label>
                    <input
                      type="text"
                      value={formData.shippingAddress.province}
                      onChange={(e) => handleInputChange('shippingAddress', 'province', e.target.value.toUpperCase())}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 ${errors['shippingAddress.province'] ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="MI"
                      maxLength={2}
                      disabled={processing}
                    />
                    {errors['shippingAddress.province'] && (
                      <p className="mt-1 text-sm text-red-600">{errors['shippingAddress.province']}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CAP *</label>
                    <input
                      type="text"
                      value={formData.shippingAddress.zipCode}
                      onChange={(e) => handleInputChange('shippingAddress', 'zipCode', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 ${errors['shippingAddress.zipCode'] ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="20100"
                      maxLength={5}
                      disabled={processing}
                    />
                    {errors['shippingAddress.zipCode'] && (
                      <p className="mt-1 text-sm text-red-600">{errors['shippingAddress.zipCode']}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Telefono *</label>
                    <input
                      type="tel"
                      value={formData.shippingAddress.phone}
                      onChange={(e) => handleInputChange('shippingAddress', 'phone', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 ${errors['shippingAddress.phone'] ? 'border-red-500' : 'border-gray-300'}`}
                      disabled={processing}
                    />
                    {errors['shippingAddress.phone'] && (
                      <p className="mt-1 text-sm text-red-600">{errors['shippingAddress.phone']}</p>
                    )}
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Note per la consegna</label>
                    <textarea
                      value={formData.shippingAddress.notes}
                      onChange={(e) => handleInputChange('shippingAddress', 'notes', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      rows={3}
                      disabled={processing}
                    />
                  </div>
                </div>
              </div>

              {/* Billing Address */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                    <User className="w-6 h-6 mr-2 text-purple-600" />
                    Indirizzo di Fatturazione
                  </h2>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.sameAsShipping}
                      onChange={(e) => handleSameAsShippingChange(e.target.checked)}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      disabled={processing}
                    />
                    <span className="ml-2 text-sm text-gray-700">Uguale all'indirizzo di spedizione</span>
                  </label>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {formData.sameAsShipping ? (
                    // Show shipping address data when same as shipping is checked
                    <div className="md:col-span-2 p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-2">L'indirizzo di fatturazione sarà uguale a quello di spedizione:</p>
                      <div className="text-sm space-y-1">
                        <p><strong>{formData.shippingAddress.firstName} {formData.shippingAddress.lastName}</strong></p>
                        {formData.shippingAddress.company && <p>{formData.shippingAddress.company}</p>}
                        <p>{formData.shippingAddress.street} {formData.shippingAddress.streetNumber}</p>
                        <p>{formData.shippingAddress.zipCode} {formData.shippingAddress.city} ({formData.shippingAddress.province})</p>
                        <p>{formData.shippingAddress.phone}</p>
                      </div>
                    </div>
                  ) : (
                    // Show billing address form when different
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Nome *</label>
                        <input
                          type="text"
                          value={formData.billingAddress.firstName}
                          onChange={(e) => handleInputChange('billingAddress', 'firstName', e.target.value)}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 ${errors['billingAddress.firstName'] ? 'border-red-500' : 'border-gray-300'}`}
                          disabled={processing}
                        />
                        {errors['billingAddress.firstName'] && (
                          <p className="mt-1 text-sm text-red-600">{errors['billingAddress.firstName']}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Cognome *</label>
                        <input
                          type="text"
                          value={formData.billingAddress.lastName}
                          onChange={(e) => handleInputChange('billingAddress', 'lastName', e.target.value)}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 ${errors['billingAddress.lastName'] ? 'border-red-500' : 'border-gray-300'}`}
                          disabled={processing}
                        />
                        {errors['billingAddress.lastName'] && (
                          <p className="mt-1 text-sm text-red-600">{errors['billingAddress.lastName']}</p>
                        )}
                      </div>
                      
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Azienda (opzionale)</label>
                        <input
                          type="text"
                          value={formData.billingAddress.company}
                          onChange={(e) => handleInputChange('billingAddress', 'company', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          disabled={processing}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Via e Numero Civico *</label>
                        <input
                          type="text"
                          value={formData.billingAddress.street || ''}
                          onChange={(e) => {
                            // Just update street field directly - no manipulation
                            handleInputChange('billingAddress', 'street', e.target.value);
                            // Clear streetNumber while typing to avoid conflicts
                            if (formData.billingAddress.streetNumber) {
                              handleInputChange('billingAddress', 'streetNumber', '');
                            }
                          }}
                          onBlur={(e) => {
                            const fullAddress = e.target.value.trim();
                            // Try to split address into street and number only when user finishes typing
                            const parts = fullAddress.split(' ');
                            if (parts.length > 1 && /^\d/.test(parts[parts.length - 1])) {
                              // Last part starts with number, consider it street number
                              const streetNumber = parts.pop();
                              const street = parts.join(' ');
                              handleInputChange('billingAddress', 'street', street);
                              handleInputChange('billingAddress', 'streetNumber', streetNumber);
                            }
                          }}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 ${errors['billingAddress.street'] || errors['billingAddress.streetNumber'] ? 'border-red-500' : 'border-gray-300'}`}
                          placeholder="Es: Via Roma 123"
                          disabled={processing}
                        />
                        {(errors['billingAddress.street'] || errors['billingAddress.streetNumber']) && (
                          <p className="mt-1 text-sm text-red-600">Via e numero civico sono richiesti</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Città *</label>
                        <input
                          type="text"
                          value={formData.billingAddress.city}
                          onChange={(e) => handleInputChange('billingAddress', 'city', e.target.value)}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 ${errors['billingAddress.city'] ? 'border-red-500' : 'border-gray-300'}`}
                          disabled={processing}
                        />
                        {errors['billingAddress.city'] && (
                          <p className="mt-1 text-sm text-red-600">{errors['billingAddress.city']}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Provincia *</label>
                        <input
                          type="text"
                          value={formData.billingAddress.province}
                          onChange={(e) => handleInputChange('billingAddress', 'province', e.target.value.toUpperCase())}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 ${errors['billingAddress.province'] ? 'border-red-500' : 'border-gray-300'}`}
                          placeholder="MI"
                          maxLength={2}
                          disabled={processing}
                        />
                        {errors['billingAddress.province'] && (
                          <p className="mt-1 text-sm text-red-600">{errors['billingAddress.province']}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">CAP *</label>
                        <input
                          type="text"
                          value={formData.billingAddress.zipCode}
                          onChange={(e) => handleInputChange('billingAddress', 'zipCode', e.target.value)}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 ${errors['billingAddress.zipCode'] ? 'border-red-500' : 'border-gray-300'}`}
                          placeholder="20100"
                          maxLength={5}
                          disabled={processing}
                        />
                        {errors['billingAddress.zipCode'] && (
                          <p className="mt-1 text-sm text-red-600">{errors['billingAddress.zipCode']}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Telefono *</label>
                        <input
                          type="tel"
                          value={formData.billingAddress.phone}
                          onChange={(e) => handleInputChange('billingAddress', 'phone', e.target.value)}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 ${errors['billingAddress.phone'] ? 'border-red-500' : 'border-gray-300'}`}
                          disabled={processing}
                        />
                        {errors['billingAddress.phone'] && (
                          <p className="mt-1 text-sm text-red-600">{errors['billingAddress.phone']}</p>
                        )}
                      </div>
                      
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Note per la fatturazione</label>
                        <textarea
                          value={formData.billingAddress.notes}
                          onChange={(e) => handleInputChange('billingAddress', 'notes', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          rows={3}
                          disabled={processing}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Payment Method */}
              <div id="payment-section" className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <CreditCard className="w-6 h-6 mr-2 text-purple-600" />
                  Modalità di Pagamento
                </h2>
                
                <div className="space-y-4">
                  <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="bank_transfer"
                      checked={formData.paymentMethod === 'bank_transfer'}
                      onChange={(e) => handlePaymentMethodChange(e.target.value)}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                      disabled={processing}
                    />
                    <div className="ml-3">
                      <div className="flex items-center">
                        <DollarSign className="w-5 h-5 mr-2 text-green-600" />
                        <span className="font-medium">Bonifico Bancario</span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Riceverai i dati per il bonifico dopo aver confermato l'ordine
                      </p>
                    </div>
                  </label>
                  
                  <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="credit_card"
                      checked={formData.paymentMethod === 'credit_card'}
                      onChange={(e) => handlePaymentMethodChange(e.target.value)}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                      disabled={processing}
                    />
                    <div className="ml-3">
                      <div className="flex items-center">
                        <CreditCard className="w-5 h-5 mr-2 text-blue-600" />
                        <span className="font-medium">Carta di Credito</span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Pagamento sicuro con Stripe - Visa, Mastercard, American Express
                      </p>
                    </div>
                  </label>
                  
                  <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="paypal"
                      checked={formData.paymentMethod === 'paypal'}
                      onChange={(e) => handlePaymentMethodChange(e.target.value)}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                      disabled={processing}
                    />
                    <div className="ml-3">
                      <div className="flex items-center">
                        <Building className="w-5 h-5 mr-2 text-yellow-600" />
                        <span className="font-medium">PayPal</span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Paga con il tuo account PayPal
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* PayPal Checkout Section */}
              {formData.paymentMethod === 'paypal' && paypalOrderData && (
                <div id="paypal-payment-section" className="bg-white rounded-lg shadow-lg p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Pagamento PayPal</h2>
                  
                  <PayPalCheckout
                    orderData={paypalOrderData}
                    onSuccess={handlePayPalSuccess}
                    onError={handlePayPalError}
                    onCancel={handlePayPalCancel}
                    disabled={processing || !paypalOrderData}
                  />
                </div>
              )}

              {/* Order Notes */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Note Aggiuntive</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Note per l'ordine</label>
                    <textarea
                      value={formData.customerNotes}
                      onChange={(e) => setFormData(prev => ({ ...prev, customerNotes: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      rows={4}
                      placeholder="Richieste speciali, istruzioni per la consegna, ecc."
                      disabled={processing}
                    />
                  </div>
                  
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.saveAddresses}
                        onChange={(e) => setFormData(prev => ({ ...prev, saveAddresses: e.target.checked }))}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        disabled={processing}
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        💾 Salva questi indirizzi per i prossimi ordini
                      </span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.newsletterOptIn}
                        onChange={(e) => setFormData(prev => ({ ...prev, newsletterOptIn: e.target.checked }))}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        disabled={processing}
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        📧 Iscrivimi alla newsletter per ricevere offerte speciali
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Submit Button - Hidden for PayPal */}
              {formData.paymentMethod !== 'paypal' && (
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <button
                    type="submit"
                    disabled={processing}
                    className={`w-full flex items-center justify-center px-6 py-4 text-lg font-semibold rounded-lg transition-colors ${
                      processing
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    {processing ? (
                      <>
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                        Elaborazione...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-6 h-6 mr-2" />
                        Conferma Ordine - €{total.toFixed(2)}
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* PayPal Information */}
              {formData.paymentMethod === 'paypal' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <div className="flex items-center space-x-2 mb-2">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-blue-900">Pagamento PayPal</h3>
                  </div>
                  <p className="text-blue-700 text-sm">
                    Completa i tuoi dati e usa il pulsante PayPal sopra per effettuare il pagamento sicuro.
                  </p>
                </div>
              )}
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Riepilogo Ordine</h3>
              
              {/* Cart Items */}
              <div className="space-y-4 mb-6">
                {cartItems.map((item) => {
                  // IMPORTANT: item.price from Builder is TOTAL price (with quantity and discounts)
                  // NOT unit price! So we need to calculate unit price correctly
                  const itemTotalPrice = item.totalPrice || item.price || 0;
                  const itemQuantity = item.quantity || 1;
                  const itemUnitPrice = itemQuantity > 0 ? itemTotalPrice / itemQuantity : 0;
                  
                  // Use EXACT same logic as Builder riepilogo
                  const selectedLiquor = item.productData?.liquor;
                  const selectedIngredients = item.productData?.ingredients || [];
                  const selectedBottle = item.productData?.bottle;
                  const labelData = item.productData?.label;
                  
                  // Build product name like Builder does
                  const productName = selectedLiquor?.name ? `${selectedLiquor.name} Personalizzato` : 'Amaro Personalizzato';
                  
                  return (
                    <div key={item.id || Math.random()} className="border-b border-gray-200 pb-4 mb-4 last:border-b-0">
                      <div className="flex justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{productName}</h4>
                          
                          <div className="text-sm text-gray-600 space-y-1 mt-2">
                            {/* Base liquor - same as Builder */}
                            {selectedLiquor && (
                              <p>
                                <span className="font-medium">Base:</span> {selectedLiquor.name}
                                {selectedLiquor.basePrice && (
                                  <span className="ml-2 text-gray-500">€{selectedLiquor.basePrice.toFixed(2)}</span>
                                )}
                              </p>
                            )}
                            
                            {/* Ingredients - same as Builder */}
                            {selectedIngredients.length > 0 && (
                              <p>
                                <span className="font-medium">Botaniche:</span> {selectedIngredients.map(ing => ing.name).join(', ')}
                              </p>
                            )}
                            
                            {/* Bottle - same as Builder */}
                            {selectedBottle && (
                              <p>
                                <span className="font-medium">Bottiglia:</span> {selectedBottle.name}
                                {selectedBottle.price > 0 && (
                                  <span className="ml-2 text-gray-500">€{selectedBottle.price.toFixed(2)}</span>
                                )}
                              </p>
                            )}
                            
                            {/* Label - same as Builder */}
                            {labelData?.text && (
                              <p>
                                <span className="font-medium">Etichetta:</span> "{labelData.text}"
                              </p>
                            )}
                            
                            <p>
                              <span className="font-medium">Quantità:</span> {itemQuantity}
                            </p>
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <p className="font-medium text-gray-900">€{itemTotalPrice.toFixed(2)}</p>
                          <p className="text-sm text-gray-600">€{itemUnitPrice.toFixed(2)} cad.</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Order Totals */}
              <div className="border-t pt-4 space-y-2">
                {/* Original price (if discounts are applied) */}
                {totalDiscount > 0 && (
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Prezzo originale</span>
                    <span className="line-through">{formatPrice(originalSubtotal)}</span>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotale</span>
                  <span>€{subtotal.toFixed(2)}</span>
                </div>
                
                {/* Quantity discount savings */}
                {totalDiscount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span className="flex items-center">
                      <Tag className="w-4 h-4 mr-1" />
                      Sconto quantità
                    </span>
                    <span className="font-medium">-{formatPrice(totalDiscount)}</span>
                  </div>
                )}
                
                {/* Coupon discount */}
                {appliedCoupon && appliedCoupon.valid && couponDiscount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span className="flex items-center">
                      <Tag className="w-4 h-4 mr-1" />
                      Coupon {appliedCoupon.coupon.code}
                    </span>
                    <span className="font-medium">-{formatPrice(couponDiscount)}</span>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Spedizione</span>
                  <span>{shippingCost === 0 ? 'Gratuita' : `€${shippingCost.toFixed(2)}`}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">IVA (22% inclusa)</span>
                  <span>€{taxAmount.toFixed(2)}</span>
                </div>
                <div className="border-t pt-2">
                  <div className="flex justify-between text-lg font-bold">
                    <span>Totale (IVA incl.)</span>
                    <span className="text-purple-600">€{total.toFixed(2)}</span>
                  </div>
                  {/* VAT breakdown */}
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>di cui IVA (22%)</span>
                      <span>€{(total * 0.22 / 1.22).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Imponibile</span>
                      <span>€{(total / 1.22).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-600 font-medium">
                  🎉 Spedizione sempre gratuita!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Checkout;